import React, { useState, useRef, useCallback } from 'react';
import Navigation from './Navigation';
import AgentChat from './AgentChat';
import VoiceOverlay from './VoiceOverlay';
import { useAgentVoice, useVoiceAutoDetect } from '../contexts/FeatureFlagsContext';
import { useVoiceConversation } from '../hooks/useVoiceConversation';
import { AudioLines } from 'lucide-react';

// The `/agent` page: page chrome + the voice pipeline. The chat itself (messages, input, send,
// markdown, error) lives in `AgentChat`; this page hands its `sendQuestion` to the voice hook
// through `sendRef` and mirrors its messages for the voice overlay.
const AgentPage = () => {
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceMessages, setVoiceMessages] = useState([]);
  const sendRef = useRef(null);

  // Stable wrapper so the voice hook can be created before AgentChat has registered its send path.
  const sendQuestion = useCallback((text, opts) => sendRef.current?.(text, opts), []);

  const agentVoiceEnabled = useAgentVoice();
  const voiceAutoDetect = useVoiceAutoDetect();
  const voice = useVoiceConversation({ sendQuestion, loop: voiceOpen, autoDetect: voiceAutoDetect });
  const showVoiceButtons = agentVoiceEnabled && voice.isSupported;

  const closeVoiceOverlay = () => {
    voice.stopAll();
    setVoiceOpen(false);
  };

  const renderVoiceButton = () =>
    /* Full-screen voice conversation (the inline single-turn mic was removed in
       favor of one voice entry point — restore from git history if wanted) */
    showVoiceButtons && (
      <button
        type="button"
        onClick={() => {
          setVoiceOpen(true);
          voice.startConversation(); // listen immediately — this click is the unlock gesture
        }}
        disabled={voice.unavailable}
        title="Voice mode"
        className="flex items-center justify-center w-10 h-10 bg-theme-primary text-content-secondary border border-border rounded-lg transition-all disabled:opacity-50 hover:text-content-primary hover:bg-theme-secondary shrink-0"
      >
        <AudioLines className="h-4 w-4" />
      </button>
    );

  return (
    <div className="min-h-screen bg-theme-primary flex flex-col">
      <Navigation />
      <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
        <AgentChat
          onSendRef={sendRef}
          onMessagesChange={setVoiceMessages}
          extraError={!voiceOpen && voice.errorMessage}
          renderInputActions={renderVoiceButton}
        />
      </div>

      {voiceOpen && (
        <VoiceOverlay
          phase={voice.phase}
          errorMessage={voice.errorMessage}
          unavailable={voice.unavailable}
          micTap={voice.micTap}
          onClose={closeVoiceOverlay}
          messages={voiceMessages}
          voiceActivity={voice.voiceActivity}
          autoDetect={voiceAutoDetect}
        />
      )}
    </div>
  );
};

export default AgentPage;
